import React, { useCallback } from "react"
import { Modal } from "@ciq/shared/components/modal"
import { Button } from "@ciq/shared/components/buttons"
import { FILE_TYPES } from "./constants"

/**
 * @typedef {Object} FileTypeModalProps
 * @property {boolean} show - Boolean indicating if modal is visible
 * @property {string} filename - Name of the file being classified
 * @property {string[]} uploadedTypes - Array of already uploaded file type keys
 * @property {(fileType: string) => void} onSelect - Callback when file type is selected
 * @property {() => void} onCancel - Callback when modal is cancelled
 */

/**
 * Modal for selecting the type of uploaded file.
 * Shows options for Room to Zone Alignment and Space Inventory.
 * Already uploaded types are disabled with a checkmark.
 *
 * @param {FileTypeModalProps} props - Component props
 * @returns {React.ReactElement|null} The modal component or null if not shown
 */
export function FileTypeModal({ show, filename, uploadedTypes, onSelect, onCancel }) {
  const handleOptionClick = useCallback((typeKey) => {
    if (!uploadedTypes.includes(typeKey)) {
      onSelect(typeKey)
    }
  }, [uploadedTypes, onSelect])

  const handleOptionKeyDown = useCallback((event, typeKey) => {
    if (uploadedTypes.includes(typeKey)) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onSelect(typeKey)
    }
  }, [uploadedTypes, onSelect])

  const fileTypeEntries = Object.values(FILE_TYPES)

  return (
    <Modal show={show} onHide={onCancel} cardClassName="zone-upload-modal">
      <div className="zone-upload-modal__header">
        <h3>Select File Type</h3>
        <p>What type of file is <strong>{filename}</strong>?</p>
      </div>

      <div className="zone-upload-modal__options">
        {fileTypeEntries.map((fileType) => {
          const isDisabled = uploadedTypes.includes(fileType.key)
          const className = [
            "zone-upload-file-type",
            isDisabled && "zone-upload-file-type--disabled"
          ].filter(Boolean).join(" ")

          return (
            <div
              key={fileType.key}
              data-testid={`file-type-option-${fileType.key}`}
              className={className}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-disabled={isDisabled}
              aria-label={`${fileType.label}: ${fileType.description}${isDisabled ? " (already selected)" : ""}`}
              onClick={() => handleOptionClick(fileType.key)}
              onKeyDown={(e) => handleOptionKeyDown(e, fileType.key)}
            >
              <div className="zone-upload-file-type__content">
                <span className="zone-upload-file-type__label">{fileType.label}</span>
                <span className="zone-upload-file-type__description">{fileType.description}</span>
              </div>
              {isDisabled && (
                <div className="zone-upload-file-type__check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.6666 5L7.49998 14.1667L3.33331 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="zone-upload-modal__footer">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </Modal>
  )
}
